<script lang="ts">
    import { STATUS } from "@/constants/statuses";
    import { shouldReturnToCurrentPageAfterAuth } from "@/utils/shouldReturnToCurrentPageAfterAuth";
    import RewardsActionButton from "@apps/rewards/components/Buttons/RewardsActionButton.svelte";
    import RewardsCustomerStatusBasedContent from "@apps/rewards/components/RewardsCustomerStatusBasedContent.svelte";
    import RewardsReferralProgramSection from "@apps/rewards/components/RewardsReferral/RewardsReferralProgramSection.svelte";
    import RewardsReferralProgramSteps from "@apps/rewards/components/RewardsReferral/RewardsReferralProgramSteps.svelte";
    import RewardsGeneralTheme from "@apps/rewards/components/Theme/RewardsGeneralTheme.svelte";
    import type RewardsAmountFormatter from "@apps/rewards/lib/RewardsAmountFormatter/RewardsAmountFormatter";
    import type RewardsCurrencyFormatter from "@apps/rewards/lib/RewardsCurrencyFormatter";
    import type RewardsProgramAccountQuery from "@apps/rewards/modules/ProgramAccount/query/RewardsProgramAccountQuery";
    import type RewardsReferralProgramQuery from "@apps/rewards/modules/ReferralProgram/queries/RewardsReferralProgramQuery";
    import type RewardsReferralProgramService from "@apps/rewards/modules/ReferralProgram/services/RewardsReferralProgramService";
    import type RewardsStorefrontValuesService from "@apps/rewards/modules/StorefrontValues/service/RewardsStorefrontValuesService";
    import type { TranslationNamespaces } from "@apps/rewards/types";
    import type GrowaveConfigurationEntity from "@lib/GrowaveConfigurationProvider/GrowaveConfigurationEntity";
    import type { AuthenticationInfoProviderInterface } from "@modules/authentication/AuthenticationInfoProvider/AuthenticationInfoProviderInterface";
    import { ROLE } from "@modules/authentication/constants";
    import type { DesignModeProviderInterface } from "@modules/common_module/DesignModeProvider/DesignModeProviderInterface";
    import type LocationControllerInterface from "@modules/common_module/LocationController/LocationControllerInterface";
    import type { URLsBuilder } from "@modules/common_module/URLsBuilder/URLsBuilder";
    import type { GwI18n } from "@modules/i18n/GwI18n/GwI18n";
    import type HTMLSanitizerInterface from "@modules/money/HTMLSanitizer/HTMLSanitizerInterface";
    import { lastValueFrom, map } from "rxjs";
    import { onMount } from "svelte";

    import Image from "@components/Image/Image.svelte";
    import T from "@components/T.svelte";
    import Text from "@components/Typography/Text.svelte";

    import RewardsPageWidgetContainer from "../../components/RewardsPageWidgetContainer.svelte";
    import RewardsPageWidgetLayout from "../../components/RewardsPageWidgetLayout.svelte";
    import RewardsPageWidgetTitle from "../../components/RewardsPageWidgetTitle.svelte";
    import RewardsReferralProgramDefaultImage from "./components/icons/rewards-referral-program.png";
    import RewardsReferralProgramPageWidgetSkeleton from "./components/skeleton/RewardsReferralProgramPageWidgetSkeleton.svelte";

    export let referralProgramService: RewardsReferralProgramService;
    export let storefrontValuesService: RewardsStorefrontValuesService;
    export let referralProgramQuery: RewardsReferralProgramQuery;
    export let programAccountQuery: RewardsProgramAccountQuery;
    export let amountFormatter: RewardsAmountFormatter;
    export let currencyFormatter: RewardsCurrencyFormatter;
    export let locationController: LocationControllerInterface;
    export let urlsBuilder: URLsBuilder;
    export let growaveConfiguration: GrowaveConfigurationEntity;
    export let authenticationInfoProvider: AuthenticationInfoProviderInterface;
    export let sanitizer: HTMLSanitizerInterface;
    export let i18n: GwI18n;
    export let namespace: TranslationNamespaces;
    export let pointsNamespace: TranslationNamespaces;
    export let customerImageUrl: string | null;
    export let designModeProvider: DesignModeProviderInterface;
    export let afterWidgetInject: () => void;

    //Services
    const {
        handleOpenReferralShareWindow,
        getReferralProgram,
        selectMailToLink,
        selectReferralSteps,
    } = referralProgramService;

    const isCustomer$ = authenticationInfoProvider
        .selectRole()
        .pipe(map((role) => role === ROLE.Customer));

    const referralData$ = programAccountQuery.referralData$;
    const referralShareChannels$ =
        storefrontValuesService.selectRewardsReferralShareChannels();
    const referralProgram$ = referralProgramQuery.referralProgram$;
    const receiverReward$ = referralProgramQuery.receiverReward$;
    const freeProductFromReceiverReward$ =
        referralProgramQuery.freeProductFromReceiverReward$;
    const referralRequestStatus$ = referralProgramQuery.status$;

    const receiverMailToLink$ = selectMailToLink({
        shopName: growaveConfiguration.shopName,
        currencySymbol: growaveConfiguration.currencySymbol,
        referralData$,
        referralShareChannels$,
        receiverReward$,
        freeProductFromReceiverReward$,
    });

    const branding$ = storefrontValuesService.selectRewardsBranding();

    function goToRegister() {
        const currentUrl = locationController.getCurrentRelativeUrl();

        const url = shouldReturnToCurrentPageAfterAuth
            ? urlsBuilder.createAuthUrlWithReturnUrl(currentUrl)
            : urlsBuilder.createRegisterUrl();

        locationController.goTo(url);
    }

    function goToLogin() {
        const currentUrl = locationController.getCurrentRelativeUrl();

        const url = shouldReturnToCurrentPageAfterAuth
            ? urlsBuilder.createAuthUrlWithReturnUrl(currentUrl)
            : urlsBuilder.createLoginUrl();

        locationController.goTo(url);
    }

    onMount(() => {
        lastValueFrom(getReferralProgram());

        afterWidgetInject();
    });

    const REWARDS_REFERRAL_PROGRAM_PAGE_WIDGET_DATA_SELECTOR =
        "rewards-referral-program-page-widget";
</script>

{#if $branding$}
    <RewardsGeneralTheme rewardsBrandingGeneral={$branding$.general}>
        <div class="gw-rd-referral-program-page-widget">
            <RewardsPageWidgetContainer>
                <RewardsPageWidgetLayout>
                    {#if $referralRequestStatus$ === STATUS.INITIAL_LOADING}
                        <RewardsReferralProgramPageWidgetSkeleton />
                    {:else}
                        <div class="gw-rd-referral-program-page-widget__layout">
                            <div
                                class="gw-rd-referral-program-page-widget__benefits"
                            >
                                <RewardsPageWidgetTitle>
                                    <T
                                        {namespace}
                                        translationKey="referral_program_benefits_title"
                                    />
                                </RewardsPageWidgetTitle>
                                <div
                                    class="gw-rd-referral-program-page-widget__benefits-description"
                                >
                                    <Text variant="base">
                                        <T
                                            {namespace}
                                            translationKey="referral_program_benefits_description"
                                        />
                                    </Text>
                                </div>
                                <div
                                    class="gw-rd-referral-program-page-widget__benefits-image"
                                >
                                    <Image
                                        src={customerImageUrl ||
                                            RewardsReferralProgramDefaultImage}
                                        alt="referral program"
                                        objectFit="cover"
                                    />
                                </div>
                            </div>
                            <div
                                class="gw-rd-referral-program-page-widget__refer-friend"
                            >
                                <RewardsPageWidgetTitle>
                                    <T
                                        {namespace}
                                        translationKey="referral_program_refer_friend_title"
                                    />
                                </RewardsPageWidgetTitle>
                                <div
                                    class="gw-rd-referral-program-page-widget__refer-friend-description"
                                >
                                    <RewardsReferralProgramSteps
                                        dataSelectorPrefix={REWARDS_REFERRAL_PROGRAM_PAGE_WIDGET_DATA_SELECTOR}
                                        steps$={selectReferralSteps(
                                            referralProgramQuery.isReferralReward$
                                        )}
                                        {namespace}
                                    />
                                    <RewardsReferralProgramSection
                                        dataSelectorPrefix={REWARDS_REFERRAL_PROGRAM_PAGE_WIDGET_DATA_SELECTOR}
                                        isCustomer={$isCustomer$}
                                        referralProgram={$referralProgram$}
                                        referralLink={$referralData$?.referralLink ??
                                            ""}
                                        receiverMailToLink={$receiverMailToLink$}
                                        referralShareChannels={$referralShareChannels$}
                                        {amountFormatter}
                                        {currencyFormatter}
                                        {urlsBuilder}
                                        {i18n}
                                        {namespace}
                                        {pointsNamespace}
                                        {sanitizer}
                                        {designModeProvider}
                                        onWindowOpen={handleOpenReferralShareWindow}
                                    />
                                </div>
                                <RewardsCustomerStatusBasedContent
                                    isCustomerContent={$isCustomer$}
                                >
                                    <div
                                        slot="default-content"
                                        class="gw-rd-referral-program-page-widget__refer-friend-btns"
                                    >
                                        <RewardsActionButton
                                            variant="primary"
                                            on:click={goToRegister}
                                        >
                                            <Text
                                                component="span"
                                                variant="base"
                                                weight="semibold"
                                            >
                                                <T
                                                    {namespace}
                                                    translationKey="referral_program_sign_up_btn_label"
                                                />
                                            </Text>
                                        </RewardsActionButton>
                                        <RewardsActionButton
                                            variant="secondary"
                                            on:click={goToLogin}
                                        >
                                            <Text
                                                component="span"
                                                variant="base"
                                                weight="semibold"
                                            >
                                                <T
                                                    {namespace}
                                                    translationKey="referral_program_log_in_btn_label"
                                                />
                                            </Text>
                                        </RewardsActionButton>
                                    </div>
                                </RewardsCustomerStatusBasedContent>
                            </div>
                        </div>
                    {/if}
                </RewardsPageWidgetLayout>
            </RewardsPageWidgetContainer>
        </div>
    </RewardsGeneralTheme>
{/if}

<style lang="postcss" global>
    @import "../../../../../../themes/default/default.css";
    @import "../../../../../../themes/globals.css";

    .gw-rd-referral-program-page-widget {
        background-color: var(--gw-rd-white-color);

        &__layout {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: var(--gw-rd-space-50);
            text-align: left;

            @media (width <= 768px) {
                gap: var(--gw-rd-space-15);
            }
        }

        &__benefits {
            flex: 1.8;
            min-width: 400px;

            @media (width <= 425px) {
                min-width: unset;
            }

            &-description {
                margin-block: var(--gw-rd-space-8)
                    clamp(
                        var(--gw-rd-space-8),
                        8.8px + 2.2vw,
                        var(--gw-rd-space-13)
                    );
                color: var(--gw-rd-secondary-color);
                font-size: clamp(14px, 11.2px + 0.89vw, 18px);
                line-height: clamp(20px, 14.25px + 1.8vw, 28px);
            }

            &-image {
                height: clamp(160px, 102.4px + 17.85vw, 240px);
                overflow: hidden;
                border-radius: var(--gw-rd-border-radius-md);

                :global(.gw-image__img) {
                    object-position: 70%;
                }
            }
        }

        &__refer-friend {
            display: flex;
            flex-direction: column;
            gap: clamp(
                var(--gw-rd-space-8),
                4.6px + 3.5vw,
                var(--gw-rd-space-16)
            );
            flex: 0.3 0 288px;
            color: var(--gw-rd-secondary-color);

            &-description {
                display: flex;
                flex-direction: column;
                gap: var(--gw-rd-space-4);
            }

            &-btns {
                display: flex;
                gap: var(--gw-rd-space-10);

                :global(.gw-btn.gw-rd-action-btn) {
                    padding: clamp(
                            var(--gw-rd-space-4),
                            3.7px + 1.3vw,
                            var(--gw-rd-space-7)
                        )
                        var(--gw-rd-space-15);
                    font-size: clamp(14px, 12.5px + 0.44vw, 16px);
                }

                :global(.gw-btn.gw-rd-action-btn--primary) {
                    width: 158px;
                }
            }
        }
    }
</style>
